
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { translations, defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { parseContentBlocks } from "@/utils/parseContentBlocks";
import { DOCUMENT_VERSIONS } from "@/lib/constants/documentVersions";

const formatVersionDate = (version: string, language: string): string => {
  const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
  try {
    return new Date(version).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

const TermsOfService = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);

  useEffect(() => {
    // Check saved language preference
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      // If no saved language, detect browser language
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = translations.legalPages[language]?.termsOfService;
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t?.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{t?.title}</h1>
          
          <div className="prose prose-slate max-w-none">
            {t?.content.map((section, index) => {
              const contentBlocks = parseContentBlocks(section.text);
              
              return (
                <div key={index} className="mb-8">
                  <h2 className="mb-4">{section.title}</h2>
                  {contentBlocks.map((block, blockIndex) => {
                    switch (block.type) {
                      case 'ordered-list':
                        return (
                          <ol key={blockIndex} className="mb-4 list-none pl-0 space-y-2">
                            {block.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="flex gap-2">
                                <span className="shrink-0 w-12">({item.itemNum || (itemIndex + 1)}.)</span>
                                <div className="flex-1">{item.text}
                                {item.nestedBullets && (
                                  <ul className="mt-2 list-disc pl-6 space-y-1">
                                    {item.nestedBullets.map((bullet, bulletIndex) => (
                                      <li key={bulletIndex} className="pl-2">
                                        {bullet}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {item.nestedNumbered && (
                                  <ol className="mt-2 list-none pl-0 space-y-1">
                                    {item.nestedNumbered.map((subItem, subIndex) => (
                                      <li key={subIndex} className="flex gap-2">
                                        <span className="shrink-0 w-12">({subItem.number})</span>
                                        <span className="flex-1">{subItem.text}</span>
                                      </li>
                                    ))}
                                  </ol>
                                )}
                                {item.additionalParagraphs && item.additionalParagraphs.length > 0 && (
                                  <div className="mt-3 space-y-2">
                                    {item.additionalParagraphs.map((para, paraIndex) => (
                                      <p key={paraIndex}>{para}</p>
                                    ))}
                                  </div>
                                )}
                                </div>
                              </li>
                            ))}
                          </ol>
                        );
                      
                      case 'unordered-list':
                        return (
                          <ul key={blockIndex} className="mb-4 list-disc pl-6 space-y-2">
                            {block.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="pl-2">
                                {item}
                              </li>
                            ))}
                          </ul>
                        );
                      
                      case 'paragraph':
                        return (
                          <p key={blockIndex} className="mb-4">
                            {block.content}
                          </p>
                        );
                      
                      case 'preformatted':
                        return (
                          <p key={blockIndex} className="mb-4 whitespace-pre-line">
                            {block.content}
                          </p>
                        );
                      
                      default:
                        return null;
                    }
                  })}
                </div>
              );
            })}
            
            <p className="text-sm text-muted-foreground mt-8">
              {language === 'de' ? 'Zuletzt aktualisiert' : 
               language === 'fr' ? 'Dernière mise à jour' : 
               language === 'es' ? 'Última actualización' : 
               'Last updated'}: {formatVersionDate(DOCUMENT_VERSIONS.termsOfService, language)}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
